import { COVERAGE_STATE_LABEL, COVERAGE_STATES, TIER_LABEL, TIERS, type AgentSummary } from "@ti/core";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Search, X } from "lucide-react";
import { useMemo } from "react";
import { z } from "zod";
import { PageHeader } from "@/components/domain/misc";
import { Page, QueryState } from "@/components/layout/page";
import { Button } from "@/components/ui/button";
import { Card, CardBody } from "@/components/ui/card";
import { Input, Select } from "@/components/ui/input";
import { AgentsTable } from "@/features/agents/agents-table";
import { totalOpen } from "@/features/agents/bits";
import { q } from "@/lib/api";
import { fmtCompact } from "@/lib/format";

const searchSchema = z.object({
  q: z.string().optional().catch(undefined),
  coverage: z.enum(COVERAGE_STATES).optional().catch(undefined),
  tier: z.enum(TIERS).optional().catch(undefined),
  /** Agent platform slug, or "in-house". */
  platform: z.string().min(1).optional().catch(undefined),
});

const IN_HOUSE = "in-house";

export const Route = createFileRoute("/_app/agents/")({
  validateSearch: searchSchema,
  component: AgentsPage,
});

function matches(a: AgentSummary, text: string) {
  const t = text.trim().toLowerCase();
  if (!t) return true;
  return [a.name, a.description, a.model, a.id, a.archetype, a.platform?.name ?? "", a.framework ?? ""].some((f) => f.toLowerCase().includes(t));
}

function AgentsPage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const agents = useQuery(q.agents());
  const setSearch = (patch: Partial<z.infer<typeof searchSchema>>) => void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });

  const filtered = useMemo(
    () =>
      agents.data?.filter(
        (a) =>
          matches(a, search.q ?? "") &&
          (!search.coverage || a.coverageState === search.coverage) &&
          (!search.tier || a.tier === search.tier) &&
          (!search.platform || (search.platform === IN_HOUSE ? !a.platform : a.platform?.slug === search.platform)),
      ),
    [agents.data, search.q, search.coverage, search.tier, search.platform],
  );
  const filtering = Boolean(search.q || search.coverage || search.tier || search.platform);
  const platforms = useMemo(() => [...new Map((agents.data ?? []).flatMap((a) => (a.platform ? [[a.platform.slug, a.platform.name] as const] : []))).entries()], [agents.data]);
  const inHouseCount = (agents.data ?? []).filter((a) => !a.platform).length;
  const all = agents.data ?? [];
  const sessions = all.reduce((n, a) => n + a.sessions24h, 0);
  const withCritical = all.filter((a) => a.openIssues.critical > 0).length;
  const withOpen = all.filter((a) => totalOpen(a.openIssues) > 0).length;

  return (
    <Page>
      <PageHeader
        title="Agents"
        description={
          agents.data
            ? `${all.length} agents in production · ${fmtCompact(sessions)} sessions in the last 24h · ${withOpen} with open issues${withCritical ? `, ${withCritical} critical` : ""}`
            : "Every agent you've built — on an agent platform or in-house — with its score, coverage state and open issues."
        }
      />

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="relative w-72">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-fg-3" aria-hidden />
          <Input
            type="search"
            aria-label="Search agents"
            placeholder="Search name, model, platform…"
            className="pl-8"
            value={search.q ?? ""}
            onChange={(e) => setSearch({ q: e.target.value || undefined })}
          />
        </div>
        <Select
          aria-label="Filter by coverage state"
          className="w-44"
          value={search.coverage ?? ""}
          onChange={(e) => setSearch({ coverage: (e.target.value || undefined) as z.infer<typeof searchSchema>["coverage"] })}
        >
          <option value="">All coverage states</option>
          {COVERAGE_STATES.map((s) => (
            <option key={s} value={s}>
              {COVERAGE_STATE_LABEL[s]}
            </option>
          ))}
        </Select>
        <Select
          aria-label="Filter by tier"
          className="w-40"
          value={search.tier ?? ""}
          onChange={(e) => setSearch({ tier: (e.target.value || undefined) as z.infer<typeof searchSchema>["tier"] })}
        >
          <option value="">All tiers</option>
          {TIERS.map((t) => (
            <option key={t} value={t}>
              {t} · {TIER_LABEL[t]}
            </option>
          ))}
        </Select>
        <Select aria-label="Filter by platform" className="w-44" value={search.platform ?? ""} onChange={(e) => setSearch({ platform: e.target.value || undefined })}>
          <option value="">All platforms</option>
          {platforms.map(([slug, name]) => (
            <option key={slug} value={slug}>
              {name} · {(agents.data ?? []).filter((a) => a.platform?.slug === slug).length}
            </option>
          ))}
          {inHouseCount ? <option value={IN_HOUSE}>In-house · {inHouseCount}</option> : null}
        </Select>
        {filtering ? (
          <Button variant="ghost" size="sm" onClick={() => setSearch({ q: undefined, coverage: undefined, tier: undefined, platform: undefined })}>
            <X /> Clear
          </Button>
        ) : null}
        {agents.data ? (
          <span className="ml-auto text-xs tabular text-fg-3" aria-live="polite">
            {filtering ? `${filtered?.length ?? 0} of ${all.length}` : `${all.length}`} agents
          </span>
        ) : null}
      </div>

      <Card>
        <CardBody className="p-0">
          {agents.error ? (
            <div className="p-4">
              <QueryState query={agents}>{() => null}</QueryState>
            </div>
          ) : (
            <AgentsTable agents={filtered} loading={agents.isPending} variant="full" emptyTitle={filtering ? "No agents match these filters" : undefined} />
          )}
        </CardBody>
      </Card>
    </Page>
  );
}
